import BaseObject from './BaseObject';
import Occasion from '../helpers/Occasion';

// These are the two providers you can actually connect on MacOS
export type AppleDeviceIntegrationProvider = 'apple-messages' | 'apple-phone-log';
// We should really just have 'apple-contacts' OR 'contacts', but there are some holdovers in user data -- so
// we need to support both in the UI.
export type DeviceIntegrationProvider =
  | 'contacts'
  | 'facebook'
  | 'linkedin'
  | 'twitter-platforms'
  | 'whatsapp'
  | 'instagram'
  | 'apple-calendar'
  | 'apple-contacts'
  | AppleDeviceIntegrationProvider;
export type RemoteIntegrationProvider =
  // | 'contacts'
  | 'google'
  // | 'linkedin'
  // | 'messages'
  | 'microsoft'
  // This is the legacy (deprecated) Twitter integration -- from before the Twitter device integration when the Twitter API was still available
  | 'twitter';
export type CSVIntegration = 'linkedin-csv' | 'csv';
export type ExternallyManagedIntegration = 'card-dav' | 'chatgpt' | 'notion' | 'zapier' | 'make';

export type IntegrationProvider =
  | RemoteIntegrationProvider
  | DeviceIntegrationProvider
  | CSVIntegration
  | ExternallyManagedIntegration;

export type IntegrationType =
  | 'email'
  | 'calendar'
  | null
  // These are the consolidated types, comes from the selector in src/Actions/user.ts
  | 'email and calendar'
  | 'calendar and email';

export type FriendType = 'all' | 'mutual' | 'followers' | 'following';

export interface SyncDeviceIntegrationOpts {
  friendType?: FriendType;
}

export interface IntegrationListResponse {
  id: string;
  provider: RemoteIntegrationProvider | DeviceIntegrationProvider;
  type: 'email' | 'calendar' | null;
  display_name: string | null;
  created: string;
  status: {
    total: number | null;
    first_import: string | null;
    last_import: string | null;
    is_syncing: boolean;
    message?: string;
    status: null | 'error';
  };
  subtypes: {
    friends?: { last_import: string | null };
    posts?: { last_import: string | null };
  } | null;
  metadata?: { version?: number };
}

export interface IntegrationListLinkedInResponse extends IntegrationListResponse {
  provider: 'linkedin';
  type: null;
}

export interface IntegrationListFacebookResponse extends IntegrationListResponse {
  provider: 'facebook';
  type: null;
}

export interface IntegrationListAppleMessagesResponse extends IntegrationListResponse {
  provider: 'apple-messages';
  type: null;
}

export interface IntegrationListTwitterResponse extends IntegrationListResponse {
  provider: 'twitter-platforms';
  type: null;
}

export interface IntegrationListWhatsAppResponse extends IntegrationListResponse {
  provider: 'whatsapp';
  type: null;
}

export interface IntegrationListInstagramResponse extends IntegrationListResponse {
  provider: 'instagram';
  type: null;
}

export interface IntegrationListGoogleResponse extends IntegrationListResponse {
  provider: 'google';
  type: 'email' | 'calendar';
}

export type IntegrationListResponseItem =
  | IntegrationListLinkedInResponse
  | IntegrationListFacebookResponse
  | IntegrationListAppleMessagesResponse
  | IntegrationListGoogleResponse
  | IntegrationListTwitterResponse
  | IntegrationListWhatsAppResponse
  | IntegrationListInstagramResponse
  | IntegrationListResponse;

export interface IntegrationStatus {
  total: number | null;
  first_import: string | null;
  last_import: string | null;
  is_syncing: boolean;
  message?: string;
  status: null | 'error';
  // for device integrations, is the integration connected...
  connected?: boolean;
}

export const titleForDeviceIntegrationType: Record<
  DeviceIntegrationProvider | 'linkedin-csv' | 'csv',
  string
> = {
  'apple-messages': 'iMessage',
  'apple-contacts': 'iOS Contacts',
  'facebook': 'Facebook',
  'linkedin': 'LinkedIn',
  'linkedin-csv': 'LinkedIn CSV',
  'contacts': 'iOS Contacts',
  'apple-calendar': 'iCloud Calendar',
  'apple-phone-log': 'Phone Calls',
  'twitter-platforms': 'Twitter',
  'whatsapp': 'WhatsApp',
  'instagram': 'Instagram',
  'csv': 'CSV',
};

export const titleForIntegrationType: Record<
  | 'google'
  | 'microsoft'
  | 'linkedin-csv'
  | 'card-dav'
  | 'chatgpt'
  | 'notion'
  | 'zapier'
  | 'make'
  | 'whatsapp'
  | 'csv',
  string
> = {
  'google': 'Google',
  'microsoft': 'Microsoft',
  'linkedin-csv': 'LinkedIn CSV',
  'card-dav': 'Address Book (CardDAV)',
  'zapier': 'Zapier',
  'make': 'Make',
  'chatgpt': 'ChatGPT',
  'notion': 'Notion',
  'whatsapp': 'WhatsApp',
  'csv': 'CSV',
};

export const integrationIsOnDevice = (provider: IntegrationProvider): boolean => {
  return [
    'linkedin',
    'apple-messages',
    'facebook',
    'apple-contacts',
    'apple-calendar',
    'apple-phone-log',
    'twitter-platforms',
    'instagram',
    'whatsapp',
  ].includes(provider);
};

export const integrationIsIOSOnly = (provider: IntegrationProvider): boolean => {
  return ['apple-contacts'].includes(provider);
};

export default class Integration extends BaseObject {
  displayName: string;
  provider: IntegrationProvider;
  type: IntegrationType;
  created: string;
  status: IntegrationStatus;
  subtypes: {
    friends?: { last_import: string | null };
    posts?: { last_import: string | null };
  } | null;
  metadata?: { version?: number; friend_type?: FriendType };

  constructor(props: Partial<IntegrationListResponseItem & { isFetching?: boolean }>) {
    super(props);

    // @ts-ignore: eventually move to only using the snake_case display_name prop
    this.displayName = props.display_name || props.displayName || '';
    this.provider = props.provider;
    this.type = props.type;
    this.created = props.created;
    this.status = props.status;
    this.subtypes = props.subtypes;
    this.metadata = props.metadata;
  }

  copyWith(props: any): Integration {
    const status = { ...(this.status || {}), ...(props.status || {}) };
    const metadata = { ...(this.metadata || {}), ...(props.metadata || {}) };
    return new Integration({ ...this, ...props, status, metadata });
  }

  get label(): string {
    let label =
      titleForDeviceIntegrationType[this.provider] ||
      titleForIntegrationType[this.provider] ||
      this.provider;

    if (this.displayName) {
      label = `${label} • ${this.displayName}`;
    }
    return label;
  }

  get isOnDevice(): boolean {
    return integrationIsOnDevice(this.provider);
  }

  get isIOSOnly(): boolean {
    return integrationIsIOSOnly(this.provider);
  }

  get hasValidScopes(): boolean {
    if (!['google', 'microsoft'].includes(this.provider)) {
      return true;
    }

    return !!this.type;
  }

  // might need to refine this...
  get lastImport(): Occasion {
    return new Occasion(this.status.last_import || this.status.first_import || this.created);
  }

  get lastSync(): Occasion {
    return this.status?.last_import ? new Occasion(this.status.last_import) : undefined;
  }
}
